<template>
    <div
        class="bond-invest-panel"
        :class="{ 'bond-invest-panel--lead': isLeadMode }"
    >
        <div class="invest-panel__content" :class="{ 'invest-panel__content--lead': isLeadMode }">
            <h2 id="bond-v2-invest-panel-title" class="invest-panel__title">{{ panelTitle }}</h2>

            <template v-if="isLeadMode">
                <p class="lead-panel__copy">
                    Share your details and we'll connect with you about this bond.
                </p>

                <div class="bonds_invest_securely bonds_invest_securely--lead">
                    <CommonButton
                        :text="isInvestLoading ? 'Processing...' : investButtonText"
                        class="invest-now-button"
                        width="full"
                        height="50px"
                        color="#002fa7"
                        :disabled="isInvestLoading"
                        :action="handleInvestNow"
                        :aria-label="investButtonText"
                        :aria-haspopup="isLeadMode ? 'dialog' : undefined"
                    />
                </div>
            </template>

            <template v-else>
                <div class="unit-selection">
                <div class="unit-selector" role="group" aria-label="Adjust investment units">
                    <button
                        class="unit-button"
                        type="button"
                        :aria-label="`Decrease investment by ${lotSize} unit${lotSize === 1 ? '' : 's'}`"
                        :disabled="units <= minUnits"
                        @click="decrementUnits"
                    >
                        -
                    </button>

                        <div class="unit-input-wrapper">
                            <input
                                id="bond-v2-investment-units"
                                type="number"
                                class="unit-input"
                                name="investment-units"
                                :value="units"
                                :min="minUnits"
                                :max="maxSelectableUnits > 0 ? maxSelectableUnits : undefined"
                                :step="lotSize"
                                inputmode="numeric"
                                autocomplete="off"
                                :aria-valuenow="units"
                                :aria-valuemin="minUnits"
                                :aria-valuemax="maxUnits"
                                aria-describedby="bond-v2-investment-units-status"
                                @input="handleUnitsInput"
                                @keydown="preventInvalidKeydown"
                                @keydown.up.prevent="incrementUnits"
                                @keydown.down.prevent="decrementUnits"
                            />
                            <label class="unit-label" for="bond-v2-investment-units">Units</label>
                            <span id="bond-v2-investment-units-status" class="sr-only" role="status" aria-live="polite">
                                Selected investment units: {{ units }}
                            </span>
                        </div>

                        <button
                            class="unit-button"
                            type="button"
                            :aria-label="`Increase investment by ${lotSize} unit${lotSize === 1 ? '' : 's'}`"
                            :disabled="maxSelectableUnits > 0 && units + lotSize > maxSelectableUnits"
                            @click="incrementUnits"
                        >
                            +
                        </button>
                    </div>
                </div>

                <div v-if="data.invest.stepMessage" class="unit-warning" role="status" aria-live="polite">
                    {{ data.invest.stepMessage }}
                </div>

                <div class="quick-add-buttons" role="group" aria-label="Quickly add multiple units">
                    <button
                        v-for="item in quickAdds"
                        :key="item.label"
                        class="quick-add-btn"
                        type="button"
                        :disabled="maxSelectableUnits > 0 && units + item.amount > maxSelectableUnits"
                        :aria-label="`Add ${item.amount} more units. New total will be ${quickAddTotal(item.amount)} units`"
                        @click="addUnits(item.amount)"
                    >
                        {{ item.label }}
                    </button>
                </div>

                <div class="trade-settlement-section">
                    <h3 class="section-title">Trade &amp; Settlement</h3>

                    <div class="trade-details-card">
                        <div class="trade-row">
                            <div class="trade-label-with-icon">
                                <img class="calendar-icon" :src="settlementDateCalendarIcon" alt="" aria-hidden="true" />
                                <span class="trade-label">Settlement date</span>
                                <VTooltip
                                    :text="tooltips.settlementDate"
                                    location="top"
                                    class="bond-invest-tooltip"
                                    aria-label="Settlement date information tooltip"
                                >
                                    <template #activator="{ props }">
                                        <button
                                            v-bind="props"
                                            type="button"
                                            class="info-icon-wrapper"
                                            :aria-label="`More information about settlement date : ${tooltips.settlementDate}`"
                                        >
                                            <VIcon class="trade-info-icon" size="14" aria-hidden="true">
                                                mdi-information-outline
                                            </VIcon>
                                        </button>
                                    </template>
                                </VTooltip>
                            </div>
                            <span class="trade-value">{{ data.invest.settlementDate }}</span>
                        </div>
                    </div>

                    <div class="trade-notice" role="note">
                        {{ data.invest.settlementNote }}
                    </div>
                </div>

                <div class="totals-section">
                    <h3 class="section-title">Totals (For Selected Units)</h3>

                    <div class="totals-card">
                        <div
                            v-for="item in data.invest.totals"
                            :key="item.label"
                            class="total-row"
                            :class="{ 'total-row--highlight': item.positive }"
                        >
                            <div class="total-label-container">
                                <span class="total-label">{{ item.label }}</span>
                                <VTooltip
                                    :text="tooltips.totals[item.label] ?? item.label"
                                    location="top"
                                    class="bond-invest-tooltip"
                                    :aria-label="`${item.label} information tooltip`"
                                >
                                    <template #activator="{ props }">
                                        <button
                                            v-bind="props"
                                            type="button"
                                            class="info-icon-wrapper"
                                            :aria-label="`More information about ${item.label}`"
                                        >
                                            <VIcon class="total-info-icon" size="14" aria-hidden="true">
                                                mdi-information-outline
                                            </VIcon>
                                        </button>
                                    </template>
                                </VTooltip>
                            </div>
                            <span class="total-value" :class="{ 'total-value--green': item.positive }">
                                {{ item.value }}
                            </span>
                        </div>
                    </div>
                </div>

                <div class="view-cashflow">
                    <button aria-label="View Cashflow" class="view-cashflow-link" type="button" @click="handleViewCashflow">
                        View Cashflow ⤻
                    </button>
                </div>

                <div class="bonds_invest_securely">
                    <CommonButton
                        :text="isInvestLoading ? 'Processing...' : investButtonText"
                        class="invest-now-button"
                        width="full"
                        height="50px"
                        color="#002fa7"
                        :disabled="isInvestLoading"
                        :action="handleInvestNow"
                        :aria-label="investButtonText"
                        :aria-haspopup="isLeadMode ? 'dialog' : undefined"
                    />
                </div>
            </template>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import settlementDateCalendarIcon from '@/assets/icons/bond-details-v2/settlement-date-calendar.svg'
import CommonButton from '@/components/common/button.vue'
import { useBondV2Data } from './bondV2Context'

interface Props {
    units: number
    minUnits: number
    maxUnits: number
    lotSize: number
    investButtonLabel?: string
    leadMode?: boolean
}

const props = defineProps<Props>()
const emit = defineEmits<{
    'units-changed': [units: number]
    'view-cashflow': [units: number]
    invest: [units: number]
}>()

const data = useBondV2Data()
const isInvestLoading = ref(false)

const isLeadMode = computed(() => props.leadMode === true)
const panelTitle = computed(() => isLeadMode.value ? 'Get in touch' : 'Invest')
const investButtonText = computed(() => props.investButtonLabel || 'Invest Securely')

const resetLoading = () => {
    isInvestLoading.value = false
}

defineExpose({
    resetLoading
})

const lotSize = computed(() => {
    const size = Math.floor(Number(props.lotSize) || 1)
    return Math.max(1, size)
})

const quickAdds = computed(() => data.value.invest.quickAdds.map((label) => {
    const multiplier = Number(label.match(/\d+/)?.[0] ?? 0)
    const amount = multiplier * lotSize.value

    return {
        label: `+${amount} Units`,
        amount
    }
}).filter((item) => item.amount > 0))

const maxSelectableUnits = computed(() => {
    if (props.maxUnits <= 0) return 0

    const units = Math.floor(props.maxUnits / lotSize.value) * lotSize.value
    return units >= props.minUnits ? units : 0
})

const clampUnits = (value: number) => {
    let units = Number(value)
    if (Number.isNaN(units)) units = props.minUnits

    units = Math.floor(units)
    units = Math.floor(units / lotSize.value) * lotSize.value

    if (units < props.minUnits) units = props.minUnits
    if (maxSelectableUnits.value > 0 && units > maxSelectableUnits.value) units = maxSelectableUnits.value

    return units
}

const updateUnits = (value: number) => {
    emit('units-changed', clampUnits(value))
}

const incrementUnits = () => updateUnits(props.units + lotSize.value)
const decrementUnits = () => updateUnits(props.units - lotSize.value)
const addUnits = (amount: number) => updateUnits(props.units + amount)
const quickAddTotal = (amount: number) => clampUnits(props.units + amount)

const handleUnitsInput = (event: Event) => {
    const target = event.target as HTMLInputElement
    const units = clampUnits(Number(target.value))
    target.value = String(units)
    emit('units-changed', units)
}

const preventInvalidKeydown = (event: KeyboardEvent) => {
    if (['e', 'E', '+', '-', '.'].includes(event.key)) {
        event.preventDefault()
    }
}

const handleInvestNow = () => {
    isInvestLoading.value = true
    emit('invest', clampUnits(props.units))
}

const handleViewCashflow = () => {
    emit('view-cashflow', clampUnits(props.units))
}

const tooltips = {
    settlementDate: 'Settlement Date: The date on which the bond is credited to your demat account and funds are deducted.',
    totals: {
        'Price per Unit': 'Price per Unit is the current market price of one bond unit, including clean price and accrued interest (if applicable).',
        'Investment amount': 'Investment Amount is the total amount you will invest in the bond.',
        'Total Interest': 'Total Interest is the total interest income you will earn over the full holding period till maturity for your selected units.',
        'Total Receivable': 'Total Receivable is the total amount you will receive at maturity, including: Principal Amount + Total Interest.'
    } as Record<string, string>
}
</script>

<style scoped>
.bond-invest-panel {
    width: 350px;
    flex: 0 0 350px;
    display: block;
    background: #F9FBFF;
    border-radius: 10px;
    padding: 20px 16px;
    box-sizing: border-box;
}

.bond-invest-panel.bond-invest-panel--lead {
    height: fit-content;
    min-height: 0;
    max-height: none;
    flex: none;
    align-self: flex-start;
    margin: 20px 0 8px;
    padding: 40px 20px;
}

.invest-panel__content {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.invest-panel__content--lead {
    gap: 14px;
}

.invest-panel__title {
    font-family: var(--font-family-primary);
    font-size: 15px;
    font-weight: 700;
    color: #1b2559;
    text-align: center;
    margin: 0;
    line-height: 24px;
}

.lead-panel__copy {
    margin: 0;
    color: #5b6684;
    font-family: var(--font-family-primary);
    font-size: 15px;
    font-weight: 400;
    line-height: 22px;
}

.unit-selection {
    width: 100%;
}

.unit-selector {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #ffffff;
    border-radius: 5px;
    padding: 12px 11px;
    gap: 16px;
}

.unit-button {
    width: 25px;
    height: 25px;
    border-radius: 4px;
    background: #eaf7ff;
    border: none;
    color: #1b2559;
    font-family: var(--font-family-primary);
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background-color 0.2s;
}

.unit-button:hover:not(:disabled) {
    background: #d0e7ff;
}

.unit-button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.unit-input-wrapper {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
}

.unit-input {
    width: 60px;
    text-align: center;
    font-family: var(--font-family-primary);
    font-size: 13px;
    font-weight: 700;
    color: #1b2559;
    line-height: 18px;
    letter-spacing: 0.455px;
    border: 1px solid transparent;
    border-radius: 4px;
    padding: 4px 8px;
    background: transparent;
    transition: all 0.2s;
}

.unit-input:focus {
    background: #ffffff;
    border-color: #005bbf;
}

.unit-input:focus-visible {
    outline: 3px solid #0027af;
    outline-offset: 2px;
}

.unit-input::-webkit-inner-spin-button,
.unit-input::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
}

.unit-input[type='number'] {
    -moz-appearance: textfield;
    appearance: textfield;
}

.unit-label {
    font-family: var(--font-family-primary);
    font-size: 13px;
    font-weight: 700;
    color: #1b2559;
    line-height: 18px;
    letter-spacing: 0.455px;
}

.sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}

.unit-warning {
    margin-top: -8px;
    color: #d93025;
    font-family: var(--font-family-primary);
    font-size: 10px;
    font-weight: 400;
    line-height: 15px;
    text-align: center;
}

.quick-add-buttons {
    display: flex;
    gap: 16px;
    width: 100%;
}

.quick-add-btn {
    flex: 1;
    background: #ffffff;
    border: none;
    border-radius: 4px;
    padding: 4px 0;
    font-family: var(--font-family-primary);
    font-size: 13px;
    font-weight: 700;
    color: #1b2559;
    cursor: pointer;
    transition: all 0.2s;
    letter-spacing: 0.455px;
    line-height: 18px;
}

.quick-add-btn:hover {
    background: #eaf7ff;
}

.quick-add-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.trade-settlement-section {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.section-title {
    font-family: var(--font-family-primary);
    font-size: 11px;
    font-weight: 500;
    color: #1b2559;
    line-height: 20px;
    margin: 0;
}

.trade-details-card {
    background: #ffffff;
    border-radius: 4px;
    padding: 8px 10px;
}

.trade-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.trade-label-with-icon {
    display: flex;
    align-items: center;
    gap: 2px;
}

.calendar-icon {
    width: 22px;
    height: 22px;
    display: block;
    margin-right: 0;
}

.trade-label {
    font-family: var(--font-family-primary);
    font-size: 15px;
    font-weight: 500;
    color: var(--color-on-surface-variant);
    line-height: 22.4px;
}

.trade-value {
    font-family: var(--font-family-primary);
    font-size: 15px;
    font-weight: 500;
    color: #1b2559;
    line-height: 23px;
}

.trade-notice {
    background: #edf9ff;
    border-radius: 4px;
    padding: 5px 4px;
    font-family: var(--font-family-primary);
    font-size: 10px;
    font-weight: 400;
    color: #1b2559;
    line-height: 15px;
    letter-spacing: 0;
}

.totals-section {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.totals-card {
    background: #ffffff;
    border-radius: 8px;
    padding: 8px 10px;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.total-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 0;
}

.total-row--highlight {
    border-top: 1px solid #eaf7ff;
}

.total-label-container {
    display: flex;
    align-items: center;
    gap: 2px;
}

.total-label {
    font-family: var(--font-family-primary);
    font-size: 15px;
    font-weight: 600;
    color: var(--color-on-surface-variant);
    line-height: 22.4px;
}

.info-icon-wrapper {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 0;
    margin-left: 0;
    transition: background-color 0.2s;
}

.info-icon-wrapper:hover {
    background-color: rgba(0, 0, 0, 0.05);
}

.trade-info-icon,
.total-info-icon {
    color: #0027af;
    flex-shrink: 0;
}

.total-value {
    font-family: var(--font-family-primary);
    font-size: 15px;
    font-weight: 500;
    color: #1b2559;
    line-height: 22.4px;
}

.total-value--green {
    color: #15803d;
}

.view-cashflow {
    display: flex;
    justify-content: center;
    margin-top: 56px;
}

.view-cashflow-link {
    background: none;
    border: none;
    color: #005bbf;
    font-family: var(--font-family-primary);
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    line-height: 10.89px;
    text-decoration: none;
    padding: 0;
}

.view-cashflow-link:hover {
    color: #005bbf;
}

.view-cashflow-link:focus-visible {
    outline: 2px solid var(--color-primary-40);
    outline-offset: 4px;
    border-radius: 4px;
}

.invest-now-button {
    width: 100%;
    background: #002fa7;
    border: none;
    border-radius: 6px;
    padding: 13px 24px;
    font-family: var(--font-family-primary);
    font-size: 16px;
    font-weight: 500;
    color: #ffffff;
    cursor: pointer;
    transition: background-color 0.2s;
    letter-spacing: 0.08px;
    line-height: 24px;
}

.invest-now-button:hover {
    background: #002080;
}

.invest-now-button:active {
    background: #001a66;
}

.bonds_invest_securely {
    margin: 0;
}

.bonds_invest_securely--lead {
    margin-top: 2px;
}

@media (max-width: 1080px) {
    .bond-invest-panel {
        width: 100%;
        flex: 1;
        max-width: 100%;
    }
}

@media (max-width: 768px) {
    .bond-invest-panel {
        border-radius: 0;
        padding: 16px 16px 88px;
        box-sizing: border-box;
    }

    .bond-invest-panel.bond-invest-panel--lead {
        height: fit-content;
        min-height: 0;
        max-height: none;
        flex: none;
        margin: 0 0 8px;
        padding: 20px 16px;
    }

    .invest-panel__content {
        gap: 14px;
    }

    .lead-panel__copy {
        font-size: 14px;
        line-height: 20px;
    }

    .bonds_invest_securely--lead {
        margin-top: 2px;
    }

    .unit-selector {
        gap: 10px;
        padding: 10px;
    }

    .quick-add-buttons {
        gap: 8px;
    }

    .trade-label,
    .trade-value,
    .total-label,
    .total-value {
        font-size: 13px;
        line-height: 18px;
    }

    .total-row {
        gap: 10px;
        align-items: flex-start;
    }

    .total-label-container {
        min-width: 0;
    }

    .total-label {
        overflow-wrap: anywhere;
    }

    .view-cashflow {
        margin-top: 16px;
    }

    .invest-now-button {
        padding: 12px 20px;
        font-size: 15px;
        line-height: 22px;
    }

    .bonds_invest_securely {
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        z-index: 240;
        margin: 0;
        padding: 14px 16px calc(14px + env(safe-area-inset-bottom));
        background: #fff;
        box-shadow: 0 -8px 24px rgba(27, 37, 89, 0.12);
        box-sizing: border-box;
    }
}
</style>

<style>
.bond-invest-tooltip .v-overlay__content,
.bond-invest-tooltip.v-tooltip .v-overlay__content {
    min-width: 200px !important;
    max-width: 300px !important;
    white-space: normal !important;
    overflow-wrap: break-word !important;
    text-align: left !important;
    font-size: 12px !important;
}

@media (max-width: 768px) {
    .bond-invest-tooltip .v-overlay__content,
    .bond-invest-tooltip.v-tooltip .v-overlay__content {
        min-width: 150px !important;
        max-width: 250px !important;
        font-size: 12px !important;
    }
}
</style>
